import { useEffect, useState } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import H from '../texts/h/H';
import P from '../texts/p/P';
import Button from '../button/Button';
import { getCasfogSubmissionDetail, SubmissionDetail } from '../../services/AnnexesService';

interface CasfogSubmissionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  submissionId: number;
}

type SubmissionStatus = 'PENDING' | 'SUCCESS' | 'ERROR' | 'TIMEOUT';

const statusConfig: Record<SubmissionStatus, { label: string; color: string }> = {
  PENDING: { label: 'Pendiente', color: '#f5a623' },
  SUCCESS: { label: 'Exitoso', color: '#4caf50' },
  ERROR: { label: 'Error', color: '#f44336' },
  TIMEOUT: { label: 'Timeout', color: '#ff9800' },
};

const CasfogSubmissionDetailModal = ({
  isOpen,
  onClose,
  submissionId,
}: CasfogSubmissionDetailModalProps) => {
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && submissionId) {
      fetchDetail();
    }
  }, [isOpen, submissionId]);

  const fetchDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCasfogSubmissionDetail(submissionId);
      setDetail(data);
    } catch (err) {
      console.error('Error fetching submission detail:', err);
      setError('Error al cargar el detalle del envío');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (detail?.downloadUrl) {
      window.open(detail.downloadUrl, '_blank');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatPeriod = (year: number, month: number) => {
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${monthNames[month - 1]} ${year}`;
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const config = statusConfig[status as SubmissionStatus];
    return (
      <span
        style={{
          backgroundColor: config?.color || '#999',
          color: 'white',
          borderRadius: '4px',
          padding: '4px 12px',
          fontSize: '14px',
          fontWeight: 'bold',
        }}
      >
        {config?.label || status}
      </span>
    );
  };

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Card display='flex' margin={{ bottom: '2' }}>
      <P type='2' variant='neutral' withMargin={false} style={{ width: '150px', fontWeight: 'bold' }}>
        {label}:
      </P>
      <P type='2' variant='neutral' withMargin={false}>
        {value}
      </P>
    </Card>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalle del Envío"
      primaryText="Cerrar"
      primaryVariant="secondary"
      onPrimaryAction={onClose}
      width="700px"
    >
      <Card margin={{ top: '3', bottom: '3' }}>
        {loading ? (
          <Card display='flex' justifyContent='center' margin={{ top: '4', bottom: '4' }}>
            <P type='2' variant='neutral'>Cargando...</P>
          </Card>
        ) : error ? (
          <Card spacing="3" variant="danger" modifier="weak" margin={{ bottom: '3' }}>
            {error}
          </Card>
        ) : detail ? (
          <>
            <Card margin={{ bottom: '4' }} display='flex' justifyContent='space-between' alignItems='center'>
              <H type='4' weight='bold' withMargin={false}>
                {detail.annexName || detail.annexCode || `Anexo ${detail.annexId}`}
              </H>
              <StatusBadge status={detail.status} />
            </Card>

            <Card margin={{ bottom: '4' }} spacing="3" variant="neutral" modifier="weak">
              <H type='5' weight='bold' withMargin>Información General</H>
              <DetailRow label="Período" value={formatPeriod(detail.year, detail.month)} />
              <DetailRow label="Tipo" value={detail.submissionType === 'ORIGINAL' ? 'Original' : 'Rectificativa'} />
              <DetailRow label="Operation ID" value={detail.operationId || '-'} />
              <DetailRow label="HTTP Status" value={detail.httpStatusCode || '-'} />
              <DetailRow label="Filas procesadas" value={detail.rowsProcessed || '-'} />
              <DetailRow label="Usuario" value={detail.submittedByUserId} />
            </Card>

            <Card margin={{ bottom: '4' }} spacing="3" variant="neutral" modifier="weak">
              <H type='5' weight='bold' withMargin>Fechas</H>
              <DetailRow label="Enviado" value={formatDate(detail.submittedAt)} />
              <DetailRow label="Cerrado" value={formatDate(detail.closedAt)} />
            </Card>

            {detail.casfogErrors && detail.casfogErrors.length > 0 && (
              <Card margin={{ bottom: '4' }} spacing="3" variant="danger" modifier="weak">
                <H type='5' weight='bold' withMargin>
                  Errores de CASFOG ({detail.casfogErrorCount})
                </H>
                <Card as="ul" style={{ margin: 0, paddingLeft: '20px' }}>
                  {detail.casfogErrors.map((err, idx) => (
                    <li key={idx}>
                      <P type='2' variant='error' withMargin={false}>
                        {err}
                      </P>
                    </li>
                  ))}
                </Card>
              </Card>
            )}

            {detail.downloadUrl && (
              <Card margin={{ top: '4' }} display='flex' justifyContent='center'>
                <Button variant='info' onClick={handleDownload}>
                  Descargar archivo CSV
                </Button>
              </Card>
            )}
          </>
        ) : null}
      </Card>
    </Modal>
  );
};

export default CasfogSubmissionDetailModal;
